<script setup lang="ts">
import { computed, ref } from 'vue'
import { vOnClickOutside } from '@vueuse/components'
import {
  mdiTruck,
  mdiWarehouse,
  mdiBriefcaseRemove,
  mdiBriefcaseAccount,
  mdiPackageVariantClosed,
  mdiPackageVariantRemove,
} from '@mdi/js'
import { useFilterStore } from '@/modules/filter/stores/filter.store'
import { useSelected } from '@/modules/motion/stores/selected.store'
import type { ClientView } from '@/api/models/types'

import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import CaretIcon from '@/shared/components/icon/CaretIcon.vue'
import FadeTransition from '@/shared/components/transitions/FadeTransition.vue'

interface Props {
  views: ClientView[]
  disabled?: boolean
}

interface Emits {
  (e: 'change'): void
}

const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const model = defineModel<number | undefined>({ required: true })

const filterStore = useFilterStore()
const selectStore = useSelected()

const isActive = ref(false)

const icons: Record<number, string> = {
  1000: mdiTruck,
  2000: mdiWarehouse,
  3000: mdiPackageVariantClosed,
  3300: mdiBriefcaseAccount,
  4000: mdiPackageVariantRemove,
  4300: mdiBriefcaseRemove,
}

const selectedView = computed(() => props.views.find((v) => v.viewId === model.value))

const viewName = computed(() => selectedView.value?.viewName ?? '')

const currentIcon = computed(() =>
  selectedView.value ? (icons[selectedView.value.viewId] ?? '') : '',
)

const updateModel = (view: ClientView): void => {
  selectStore.selection.clear()
  selectStore.lastSelectedId = null
  filterStore.clearTag()

  model.value = view.viewId
  isActive.value = false

  emits('change')
}

const onToggle = (): void => {
  isActive.value = !isActive.value
}
</script>

<template>
  <div class="relative w-full" v-on-click-outside="() => (isActive = false)">
    <div class="flex items-center border-x border-gray-800 bg-transparent">
      <button
        type="button"
        class="flex items-center w-full cursor-pointer text-sm px-2 py-1 gap-2 text-left text-white disabled:bg-white disabled:text-gray-400"
        :title="selectedView?.viewName"
        :disabled="disabled"
        @click="onToggle"
      >
        <SvgIcon
          v-if="currentIcon"
          class="fill-white dark:fill-brand h-5 w-5 shrink-0"
          :path="currentIcon"
        />
        <span class="truncate flex-1">
          {{ viewName }}
        </span>
        <CaretIcon
          v-if="views.length > 1"
          class="w-5 h-5 shrink-0"
          :active="isActive"
          :title="selectedView?.viewName"
        />
      </button>
    </div>
    <FadeTransition>
      <div
        v-if="views.length > 1 && isActive"
        class="z-40 absolute mt-1.5 border-x border-b border-gray-300 bg-white dark:border-gray-800 dark:bg-black w-full overflow-x-hidden max-h-64 focus:outline-none shadow-md"
      >
        <ul class="cursor-pointer dark:bg-black">
          <template v-for="(view, index) in views" :key="index">
            <li
              v-if="view.viewName !== selectedView?.viewName"
              class="group text-sm truncate dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-white p-2"
              @click="updateModel(view)"
            >
              <div class="flex items-center">
                <SvgIcon
                  class="fill-gray-700 h-5 w-5 me-1.5 dark:fill-gray-400 dark:group-hover:fill-white"
                  :path="icons[view.viewId]"
                />
                <div class="truncate text-sm">{{ view.viewName }}</div>
              </div>
            </li>
          </template>
        </ul>
      </div>
    </FadeTransition>
  </div>
</template>
